<template>
  <div class="container" :style="computedOpacity">
    <div class="imgContainer" :class="{ nft: !!props.nftToken }">
      <img :src="computedImage" alt="token image" />
    </div>
    <div class="infoContainer">
      <div v-if="computedTokenName" class="tokenName">{{ computedTokenName }}</div>
      <p v-if="computedOwnedValue !== null" class="ownedValue">
        {{ computedOwnedValue }}
      </p>
      <p v-if="computedEarnedValue !== null" class="earnedValue">{{ computedEarnedValue }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { Tokens, tokensConfig } from '~/utils/constants';
import tokens from '~/public/img/tokens';
import type { StorageItem as StorageItemType } from '~/types/apiService';
import type { TokenDescription } from '~/types/token';
import { Formatters } from '~/utils/formatters/formatters';
import BigNumber from 'bignumber.js';

const { t } = useI18n();
const props = defineProps<{
  tokenMedia?: string;
  ownedValue?: string;
  earnedValue?: string;
  tokenName?: string;
  token?: StorageItemType;
  nftToken?: TokenDescription;
}>();

const computedImage = computed(() => {
  if (props.tokenMedia) {
    return props.tokenMedia;
  }
  if (props.token) {
    return tokens[props.token.key as Tokens]?.toString() || '';
  }
  if (props.nftToken) {
    return props.nftToken.imageUrl;
  }
  return '';
});

const computedTokenName = computed(() => {
  if (props.tokenName) {
    return props.tokenName;
  }
  if (props.token) {
    return t(tokensConfig[props.token.key]?.label || '', { count: 0 });
  }
  if (props.nftToken) {
    return t(Formatters.text.formatTranslationKey(props.nftToken.name) || '', { count: 0 });
  }
  return '';
});

function formatAmount(amount: string) {
  const number = new BigNumber(amount);

  if (number.isNaN() || number.lt(0) || number.isZero()) {
    return '0';
  }

  if (number.lt(0.01)) {
    return '<0.01';
  }

  if (number.gte(10000)) {
    return number.toFormat(0);
  }

  return number.toFormat(2);
}

const computedOwnedValue = computed(() => {
  if (props.nftToken) return null;

  if (props.ownedValue !== undefined) {
    return formatAmount(props.ownedValue);
  }
  if (props.token) {
    return formatAmount(props.token.balance);
  }
  return '';
});

const computedEarnedValue = computed(() => {
  if (props.earnedValue !== undefined) {
    return formatAmount(props.earnedValue);
  }
  if (props.token) {
    const secondaryTokens = [Tokens.blackPearl, Tokens.crystallineOre, Tokens.shiningMetalite, Tokens.acorn];
    if (props.token.isSellable || secondaryTokens.includes(props.token.key)) {
      return formatAmount(props.token.earnedBalance);
    }
    return null;
  }
  if (props.nftToken) {
    return '1';
  }
  return null;
});

const computedOpacity = computed(() => {
  if (props.nftToken) return {};

  let ownedNumeric = 0;

  if (props.ownedValue !== undefined) {
    ownedNumeric = Number(props.ownedValue);
  } else if (props.token) {
    ownedNumeric = Number(props.token.balance);
  }

  return ownedNumeric === 0 ? { opacity: '0.15' } : {};
});
</script>

<style lang="scss" scoped>
.container {
  border-radius: 12px;
  border: 1px solid #1e353e;
  background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.5) 0%, rgba(0, 0, 0, 0) 100%),
    linear-gradient(0deg, #0d242f, #0d242f);
  box-shadow: 2px 4px 5px 0px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(60px);
  padding: 10px;
  width: 100%;
  min-width: 150px;
  max-width: 160px;
  min-height: 206px;
  display: flex;
  align-items: center;
  flex-direction: column;
  line-height: 1;
  opacity: 0.85;

  .imgContainer {
    width: 104px;
    max-height: 104px;
    border-radius: 12px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .imgContainer.nft {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .infoContainer {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .tokenName {
    margin: 14px 0;
    font-family: Eczar;
    font-weight: 700;
    font-size: 16px;
    text-align: center;
    color: #ffffff;
    min-height: 40px;
  }

  &:hover:not(.selected) {
    background: radial-gradient(102.4% 102.35% at 57.35% 110.94%, #0083ad 0%, rgba(0, 0, 0, 0) 100%);
  }

  &:not(.selected) {
    cursor: pointer;
  }
}

.ownedValue {
  font-family: Eczar;
  font-weight: 400;
  font-size: 20px;
  text-align: center;
  color: #90989d;
  margin-bottom: 4px;
}

.earnedValue {
  font-family: Eczar;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
  color: #ffffff;
}
</style>
